import { useState } from 'react'
import { Role, Teammate } from 'types'
import { useAuth } from 'components/Auth'
import { Menu, MenuButton } from '../Dashboard/Menu'
import RevertToCoach from './RevertToCoach'
import RemoveTeammate from './RemoveTeammate'
import LeaveTeam from './LeaveTeam'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { UserList, UserListItem } from 'components/Admin/Shared/UserList'
import { useAffiliate } from 'hooks/useAffiliate'

type TeammateListItemProps = {
  teammate: Teammate
  isMobile: boolean
}

function TeammateListItem({ teammate, isMobile }: TeammateListItemProps) {
  const { firstName, lastName, email, id, certifications, roles } = teammate
  const { user } = useAuth()
  const { selectedAffiliate: affiliate } = useAffiliate()
  const isMe = user.id === id
  const canLeaveTeam = isMe && !roles.includes(Role.LOR)
  const canRemoveTeammate =
    !isMe &&
    !roles.includes(Role.LOR) &&
    affiliate.roles.some((r) => [Role.LOR, Role.Manager].includes(r as Role))
  const canRevertToCoach =
    !isMe &&
    roles.includes(Role.Manager) &&
    affiliate.roles.some((r) => [Role.LOR, Role.Manager].includes(r as Role))

  const [showRemoveTeammate, setShowRemoveTeammate] = useState(false)
  const [showRevertToCoach, setShowRevertToCoach] = useState(false)
  const [showLeaveTeam, setShowLeaveTeam] = useState(false)

  return (
    <>
      {showRevertToCoach && (
        <RevertToCoach
          onClose={() => setShowRevertToCoach(false)}
          teammate={teammate}
        />
      )}

      {showRemoveTeammate && (
        <RemoveTeammate
          onClose={() => setShowRemoveTeammate(false)}
          teammate={teammate}
        />
      )}

      {showLeaveTeam && (
        <LeaveTeam
          onClose={() => setShowLeaveTeam(false)}
          teammate={teammate}
        />
      )}

      <UserListItem
        isMobile={isMobile}
        isMe={isMe}
        name={firstName + ' ' + lastName}
        email={email}
        roles={roles}
        certifications={certifications}
        showExpiration
        menu={
          (canRevertToCoach || canLeaveTeam || canRemoveTeammate) && (
            <Menu>
              {canRevertToCoach && (
                <MenuButton onClick={() => setShowRevertToCoach(true)}>
                  Revert to Coach
                </MenuButton>
              )}
              {canLeaveTeam && (
                <MenuButton
                  onClick={() => setShowLeaveTeam(true)}
                  variant='destructive'
                >
                  Leave Team
                </MenuButton>
              )}
              {canRemoveTeammate && (
                <MenuButton
                  onClick={() => setShowRemoveTeammate(true)}
                  variant='destructive'
                >
                  Remove Teammate
                </MenuButton>
              )}
            </Menu>
          )
        }
      />
    </>
  )
}

type Props = {
  teammates: Teammate[]
}

function TeammatesTable({ teammates }: Props) {
  const isMobile = useMediaQuery(`(max-width: 767px)`)

  return (
    <UserList isMobile={isMobile} showExpiration>
      {teammates?.map((teammate) => (
        <TeammateListItem
          key={teammate.id}
          teammate={teammate}
          isMobile={isMobile}
        />
      ))}
    </UserList>
  )
}

export default TeammatesTable
